import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';

import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';

interface FormDialogProps {
  open: boolean;
  title: string;
  description?: string;
  /** Something typed and not saved: closing asks first. */
  dirty: boolean;
  pending: boolean;
  /** The server's sentence for a refused save, shown at the top and focused. */
  failure: string | null;
  submitLabel: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
  maxWidth?: 'xs' | 'sm' | 'md';
  /** The dialog's ID; the form, the alert and the buttons are named from it. */
  testId: string;
  children: ReactNode;
}

/**
 * The ARC Builder's form dialogs, in one shape: the fields, a Cancel and a
 * save button, the server's refusal at the top (moved to, so a screen reader
 * reads it), and a "Leave without saving?" question when something typed
 * would be lost - the Day 18 rule, as the Invite user dialog keeps it.
 */
export function FormDialog({
  open,
  title,
  description,
  dirty,
  pending,
  failure,
  submitLabel,
  onSubmit,
  onClose,
  maxWidth = 'sm',
  testId,
  children,
}: FormDialogProps) {
  const formId = useId();
  const alertRef = useRef<HTMLDivElement>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);

  useEffect(() => {
    if (failure) alertRef.current?.focus();
  }, [failure]);

  const askToClose = () => {
    if (pending) return;
    if (dirty) setConfirmingClose(true);
    else onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={askToClose}
      title={title}
      description={description}
      maxWidth={maxWidth}
      testId={testId}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={askToClose}
            disabled={pending}
            data-testid={`${testId}-cancel-button`}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            loading={pending}
            data-testid={`${testId}-save-button`}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <Stack
        component="form"
        id={formId}
        spacing={2.5}
        aria-label={title}
        data-testid={`${testId}-form`}
        noValidate
        onSubmit={onSubmit}
        sx={{ pt: 0.5 }}
      >
        {failure ? (
          <Alert
            severity="error"
            role="alert"
            ref={alertRef}
            tabIndex={-1}
            data-testid={`${testId}-error`}
          >
            {failure}
          </Alert>
        ) : null}
        {children}
      </Stack>

      <UnsavedChangesDialog
        blocked={confirmingClose}
        stay={() => setConfirmingClose(false)}
        leave={() => {
          setConfirmingClose(false);
          onClose();
        }}
      />
    </Dialog>
  );
}
